import { useEffect, useRef, useState } from "react";
import api, { formatErr, API } from "@/lib/api";
import { Upload, Loader2, CheckCircle2, AlertCircle, FileText, History, Cloud, RefreshCw } from "lucide-react";

export default function SyncEvents() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);
  const [stats, setStats] = useState(null);
  const [apiSyncing, setApiSyncing] = useState(false);
  const [apiSyncResult, setApiSyncResult] = useState(null);
  const [apiSyncError, setApiSyncError] = useState("");
  const fileInput = useRef(null);

  const loadStats = async () => {
    const r = await api.get("/recipients/stats");
    setStats(r.data);
  };

  useEffect(() => {
    api.get("/campaigns").then(r => setCampaigns(r.data || []));
    loadStats();
  }, []);

  const runApiSync = async () => {
    setApiSyncing(true); setApiSyncResult(null); setApiSyncError("");
    try {
      const { data } = await api.post("/mailgun/sync-suppressions");
      setApiSyncResult(data);
      await loadStats();
    } catch (e) {
      setApiSyncError(formatErr(e.response?.data?.detail));
    } finally {
      setApiSyncing(false);
    }
  };

  const upload = async (file) => {
    setUploading(true); setResult(null); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = campaignId ? { campaign_id: campaignId } : {};
      const { data } = await api.post("/sync/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      setResult({ ...data, filename: file.name });
      await loadStats();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const downloadExport = (filter) => {
    const token = localStorage.getItem("rahi_token");
    const url = `${API}/recipients/export?filter=${filter}`;
    // Trigger fetch with auth header, then save as blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `rahi_recipients_${filter}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      });
  };

  return (
    <div className="px-12 py-10 max-w-[1100px]" data-testid="sync-events-page">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--rahi-red)" }}>
          Backfill · Mailgun
        </p>
        <h1 className="font-display text-5xl font-black tracking-tight leading-none">Sync events</h1>
        <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--rahi-muted)" }}>
          Reconcile bounces &amp; unsubscribes into your recipient list. Preferred: click &ldquo;Sync now&rdquo;
          to pull directly from Mailgun&rsquo;s API. Fallback: upload a CSV export from Mailgun&rsquo;s dashboard.
          Both are idempotent — safe to run repeatedly.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-10">
          <Stat label="Total" value={stats.total}/>
          <Stat label="Active" value={stats.active} accent/>
          <Stat label="Unsubscribed" value={stats.unsubscribed}/>
          <Stat label="Bounced" value={stats.bounced}/>
        </div>
      )}

      {/* API sync (one-click) — preferred when Mailgun API is reachable from our server */}
      <div className="card p-6 mb-6" data-testid="api-sync-card">
        <div className="flex items-start justify-between gap-6 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Cloud size={18} style={{ color: "var(--rahi-red)" }}/>
              <h2 className="font-display text-xl font-black tracking-tight">Sync from Mailgun API</h2>
            </div>
            <p className="text-sm" style={{ color: "var(--rahi-muted)" }}>
              One click — pulls all bounces, unsubscribes, and complaints from Mailgun and marks matching
              recipients in your list. Use this after every send, especially when webhooks failed to
              deliver. Idempotent — safe to run repeatedly.
            </p>
          </div>
          <button
            onClick={runApiSync}
            disabled={apiSyncing}
            className="btn btn-primary flex items-center gap-2 shrink-0"
            data-testid="api-sync-btn"
          >
            {apiSyncing ? <><Loader2 size={14} className="animate-spin"/> Syncing…</> : <><RefreshCw size={14}/> Sync now</>}
          </button>
        </div>
        {apiSyncResult && (
          <div className="mt-4 p-4 rounded-md" style={{ background: apiSyncResult.ok ? "#F0FDF4" : "#FEF3C7", border: `1px solid ${apiSyncResult.ok ? "#BBF7D0" : "#FDE68A"}` }} data-testid="api-sync-result">
            <div className="flex items-start gap-2 mb-3">
              {apiSyncResult.ok
                ? <CheckCircle2 size={16} style={{ color: "#047857" }}/>
                : <AlertCircle size={16} style={{ color: "#B45309" }}/>}
              <div className="text-sm font-medium">
                {apiSyncResult.ok ? "Sync complete" : "Sync partial — see errors below"}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-xs mb-3">
              <div>
                <div className="uppercase tracking-wider mb-1" style={{ color:"var(--rahi-soft)" }}>Bounces</div>
                <div>Fetched: <span className="font-mono">{(apiSyncResult.fetched?.bounces || 0).toLocaleString("en-IN")}</span></div>
                <div>Newly marked: <span className="font-mono font-bold" style={{ color:"var(--rahi-red)" }}>{(apiSyncResult.applied?.bounced_marked || 0).toLocaleString("en-IN")}</span></div>
              </div>
              <div>
                <div className="uppercase tracking-wider mb-1" style={{ color:"var(--rahi-soft)" }}>Unsubscribes</div>
                <div>Fetched: <span className="font-mono">{(apiSyncResult.fetched?.unsubscribes || 0).toLocaleString("en-IN")}</span></div>
                <div>Newly marked: <span className="font-mono font-bold" style={{ color:"var(--rahi-red)" }}>{(apiSyncResult.applied?.unsubscribed_marked || 0).toLocaleString("en-IN")}</span></div>
              </div>
              <div>
                <div className="uppercase tracking-wider mb-1" style={{ color:"var(--rahi-soft)" }}>Complaints</div>
                <div>Fetched: <span className="font-mono">{(apiSyncResult.fetched?.complaints || 0).toLocaleString("en-IN")}</span></div>
                <div>Newly marked: <span className="font-mono font-bold" style={{ color:"var(--rahi-red)" }}>{(apiSyncResult.applied?.complained_marked || 0).toLocaleString("en-IN")}</span></div>
              </div>
            </div>
            {apiSyncResult.errors && Object.keys(apiSyncResult.errors).length > 0 && (
              <div className="mt-3 pt-3 border-t border-[color:var(--rahi-border)] text-xs">
                <div className="font-medium mb-1" style={{ color:"#B45309" }}>Mailgun errors — falling back to CSV upload below may help:</div>
                {Object.entries(apiSyncResult.errors).map(([kind, msg]) => (
                  <div key={kind} className="font-mono mt-1" style={{ color:"var(--rahi-muted)" }}>
                    <span className="uppercase">{kind}:</span> {msg}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {apiSyncError && (
          <div className="mt-3 text-sm p-3 rounded-md" style={{ background:"#FEF2F2", color:"#B91C1C" }} data-testid="api-sync-error">
            {apiSyncError}
          </div>
        )}
      </div>

      {/* CSV upload — manual fallback when API key/domain access is blocked */}
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.14em]" style={{ color: "var(--rahi-soft)" }}>
        <div className="h-px flex-1" style={{ background: "var(--rahi-border)" }}/>
        <span>or upload CSV manually</span>
        <div className="h-px flex-1" style={{ background: "var(--rahi-border)" }}/>
      </div>

      {/* Campaign selector */}
      <div className="card p-5 mb-6">
        <div className="text-[11px] uppercase tracking-[0.12em] mb-2" style={{ color: "var(--rahi-muted)" }}>
          Attribute to campaign (optional)
        </div>
        <select
          value={campaignId} onChange={(e)=>setCampaignId(e.target.value)}
          className="input" data-testid="sync-campaign-select"
        >
          <option value="">— Don&apos;t attribute (recipients-only update) —</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
          ))}
        </select>
        <p className="hint mt-2">
          If you pick a campaign, the bounced/unsubscribed counters on that campaign will be incremented.
        </p>
      </div>

      {/* Dropzone */}
      <div
        className={`dropzone mb-8 ${drag ? "drag-over" : ""}`}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid="sync-dropzone"
      >
        <input
          ref={fileInput} type="file" accept=".csv" className="hidden"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          data-testid="sync-file-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <Loader2 size={16} className="animate-spin"/> Processing CSV…
          </div>
        ) : (
          <>
            <Upload size={22} className="mx-auto mb-3" style={{ color: "var(--rahi-soft)" }}/>
            <div className="font-medium text-sm">Drop Mailgun CSV here or click to upload</div>
            <div className="text-xs mt-1" style={{ color: "var(--rahi-soft)" }}>
              Auto-detects bounce vs unsubscribe by columns · Required: <span className="font-mono">address</span>
            </div>
          </>
        )}
      </div>

      {/* Result */}
      {result && (
        <div className="card p-6 mb-8" data-testid="sync-result">
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle2 size={20} style={{ color: "#047857" }}/>
            <div>
              <div className="font-medium text-base">
                {result.kind === "bounced" ? "Bounce" : "Unsubscribe"} sync complete
              </div>
              <div className="text-xs mt-0.5 font-mono" style={{ color: "var(--rahi-muted)" }}>
                {result.filename}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-4">
            <ResultStat label="Rows in CSV" value={result.total_rows}/>
            <ResultStat label="Unique emails" value={result.unique_emails}/>
            <ResultStat label="Newly marked" value={result.newly_marked} accent/>
            <ResultStat label="Already marked" value={result.already_marked}/>
          </div>
          {result.not_in_list > 0 && (
            <div className="mt-4 text-xs flex gap-2 items-center" style={{ color: "var(--rahi-muted)" }}>
              <AlertCircle size={13}/> {result.not_in_list.toLocaleString("en-IN")} emails in CSV were not found in your recipient list (skipped).
            </div>
          )}
          {result.campaign_updated && (
            <div className="mt-2 text-xs flex gap-2 items-center" style={{ color: "#047857" }}>
              <CheckCircle2 size={13}/> Campaign stats updated.
            </div>
          )}
        </div>
      )}
      {error && (
        <div className="text-sm mb-6 p-4 rounded-md" style={{ background:"#FEF2F2", color:"#B91C1C" }}>
          {error}
        </div>
      )}

      {/* Export cleaned list */}
      <div className="card p-6 mt-10">
        <div className="flex items-start justify-between gap-6 mb-5">
          <div>
            <h2 className="font-display text-2xl font-black tracking-tight mb-1">Download cleaned list</h2>
            <p className="text-sm" style={{ color: "var(--rahi-muted)" }}>
              Export your active recipients (bounces &amp; unsubscribes removed) as a clean CSV.
              Use this when adding new IDs from external sources so duplicates and known-bad
              emails are filtered out before re-import.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={()=>downloadExport("active")} className="btn btn-primary" data-testid="export-active-btn">
            <FileText size={14}/> Download active list ({stats?.active?.toLocaleString("en-IN") || 0})
          </button>
          <button onClick={()=>downloadExport("bounced")} className="btn btn-ghost" data-testid="export-bounced-btn">
            <History size={14}/> Bounced ({stats?.bounced?.toLocaleString("en-IN") || 0})
          </button>
          <button onClick={()=>downloadExport("unsubscribed")} className="btn btn-ghost" data-testid="export-unsub-btn">
            <History size={14}/> Unsubscribed ({stats?.unsubscribed?.toLocaleString("en-IN") || 0})
          </button>
          <button onClick={()=>downloadExport("all")} className="btn btn-ghost" data-testid="export-all-btn">
            <FileText size={14}/> Everything ({stats?.total?.toLocaleString("en-IN") || 0})
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card p-5">
      <div className="text-[11px] uppercase tracking-[0.12em] mb-2" style={{ color: "var(--rahi-soft)" }}>{label}</div>
      <div className="font-display text-3xl font-black tracking-tight" style={{ color: accent ? "var(--rahi-red)" : "var(--rahi-ink)" }}>
        {value.toLocaleString("en-IN")}
      </div>
    </div>
  );
}

function ResultStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color:"var(--rahi-soft)" }}>{label}</div>
      <div className="font-display text-2xl font-black tracking-tight" style={{ color: accent ? "var(--rahi-red)" : "var(--rahi-ink)" }}>
        {value.toLocaleString("en-IN")}
      </div>
    </div>
  );
}
